// src/Pages/Events/EventListPage.tsx
import React, { useEffect, useState } from "react";
import { Center, Loader } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import EventCard from "../../components/EventCard";
import HeroSectionV2 from "../../components/HeroSectionV2";
import { getEventTargetPath } from "../../utils/eventRoutes";
import { setPostLoginRedirect, type EntryPoint } from "../../utils/authRedirect";
import Billede1 from '../../Images/Billede1.jpg';
import EventCardV2 from "../../components/EventCardV2";
import { EventDto } from "../Admin/ID/types";
import { useLanguage } from "../../context/LanguageContext";

interface Event {
    eventID: number;
    eventName: string;
    eventLogoFilID: string;
    eventLogoPath: string;
    startTime: string;
    endTime: string;
    Active: boolean;
}

type Props = {
    entry: EntryPoint; // "volunteer" | "team" | "media" | "intern"
    title?: string;
    description?: string;
    internalFilter?: boolean | null;
};

const EventListPage: React.FC<Props> = ({ entry, title, description, internalFilter = false, }) => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { setLanguage } = useLanguage();

    useEffect(() => {
        setLanguage("en");
    }, [setLanguage]);

    useEffect(() => {
        const apiUrl = process.env.REACT_APP_API_URL;
        fetch(`${apiUrl}/api/events`)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP error! Status: ${r.status}`);
                return r.json();
            })
            .then((data) => {
                setEvents(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching events:", err);
                setLoading(false);
            });
    }, []);

    const filteredEvents = events.filter((event) => {
        if (internalFilter === null || internalFilter === undefined) {
            return true;
        }

        return event.isInternal === internalFilter;
    });

    const pageConfig = {
        team: {
            titleEn: "Team Page",
            titleDa: "Team Side",
            descriptionEn: "Team Management...",
            descriptionDa: "Team Administration...",
            badgeText: "TEAM"
        },

        media: {
            titleEn: "Media Page",
            titleDa: "Medie Side",
            descriptionEn: "Media Management...",
            descriptionDa: "Medie Administration...",
            badgeText: "MEDIA"
        },

        intern: {
            titleEn: "Internal Page",
            titleDa: "Intern Side",
            descriptionEn: "Internal Events...",
            descriptionDa: "Interne Events...",
            badgeText: "INTERN"
        },

        club: {
            titleEn: "Club Page",
            titleDa: "Klub Side",
            descriptionEn: "Club Management...",
            descriptionDa: "Klub Administration...",
            badgeText: "CLUB"
        },

        volunteer: {
            titleEn: "Volunteer Page",
            titleDa: "Frivillig Side",
            descriptionEn: "Volunteer Events...",
            descriptionDa: "Frivillige Events...",
            badgeText: "VOLUNTEER"
        }
    } as const;

    const config = pageConfig[entry] ?? pageConfig.team;

    

    function getTargetAfterLogin(entry: string, eventId: number) {
        switch (entry) {
            case "team":
                return `/TeamDashboard/${eventId}`;     // eller /TeamDashboard/${eventId} hvis du har den route
            case "media":
                return `/MediaDashboard/${eventId}`;
            case "club":
                return `/ClubDashboard/${eventId}`;
            case "intern":
                return `/InternDashboard/${eventId}`;   // hvis du laver den protected
            case "volunteer":
                return `/VolunteerGroupsDashboard/${eventId}`;   // ✅ ADD THIS
            default:
                return `/EventPage/${eventId}`;
        }
    }


    const handleSelectEvent = (eventId: number) => {
        const isLoggedIn = !!sessionStorage.getItem("authToken");

        if (!isLoggedIn) {
            navigate(`/login?entry=${entry}&eventId=${eventId}`);
            return;
        }

        // hvis logget ind:
        navigate(getTargetAfterLogin(entry, eventId));
    };


    return (
        <div className="role-page">

            <HeroSectionV2
                titleEn={config.titleEn}
                titleDa={config.titleDa}
                descriptionEn={config.descriptionEn}
                descriptionDa={config.descriptionDa}
                imageSrc={Billede1}
                badgeText={config.badgeText}
                logoSrc=""
            />
        <section className="events-section">
            {loading && (
                <Center mt="md">
                    <Loader />
                </Center>
            )}

                    <div className="events-grid">
                        {filteredEvents.map((event) => (
                            <EventCardV2
                                key={event.eventID}
                                eventId={event.eventID}
                                eventName={event.eventName}
                                eventLogo={event.eventLogoPath}
                                startTime={event.startTime}
                                endTime={event.endTime}
                                showDate={false}
                                onSignup={handleSelectEvent}
                                badgeText={config.badgeText}
                                isActive={event.active}
                                signupTextDa="Vælg Event"
                                signupTextEn="Choose Event"
                            />
                        ))}
                    </div>
            </section>
        </div>
    );
};

export default EventListPage;
